import { ONETRUST_CATEGORIES, OneTrustCategory } from './index';
import { parseOptanonConsentGroups } from './parseOptanonConsent';

/**
 * Pre-subscription consent check for browser code that needs to gate behavior
 * before the OneTrust SDK has fired `OptanonWrapper` for the first time.
 *
 * Reads the same `OptanonConsent` cookie OneTrust writes after a banner choice,
 * plus the GPC signal exposed by Firefox. Once the SDK loads and the central
 * dispatcher in `index.ts` starts receiving updates, prefer
 * `onConsentChange` / `hasConsent` — this helper is only the bridge for the
 * narrow window before that subscription delivers a value.
 *
 * Defaults to **opt-in** when neither signal is present so a slow OneTrust load
 * does not silently drop legitimate events. Mirrors the default in
 * `serverConsent.ts` for server-side code.
 */

/** Pull a named cookie's value out of a `document.cookie`-style string. */
export function readCookie(cookieJar: string, name: string): string | undefined {
  if (!cookieJar) return undefined;
  for (const part of cookieJar.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(`${name}=`)) {
      return trimmed.slice(name.length + 1);
    }
  }
  return undefined;
}

interface ClientConsentInputs {
  /** Defaults to `document.cookie` when omitted. */
  cookieJar?: string;
  /** Defaults to `navigator.globalPrivacyControl` when omitted. */
  gpc?: boolean;
}

/**
 * Returns whether the given category is consented to, derived from the
 * `OptanonConsent` cookie and GPC. Returns `true` (default opt-in) when there
 * is no explicit signal in either direction. Strictly Necessary (C0001) is
 * always granted — same contract as `serverConsent.ts`.
 */
export function hasConsentFromBrowser(
  category: OneTrustCategory,
  { cookieJar, gpc }: ClientConsentInputs = {},
): boolean {
  if (typeof window === 'undefined') return true;
  if (category === ONETRUST_CATEGORIES.STRICTLY_NECESSARY) return true;

  const jar = cookieJar ?? window.document?.cookie ?? '';
  const optanonCookie = readCookie(jar, 'OptanonConsent');

  if (optanonCookie) {
    const groups = parseOptanonConsentGroups(optanonCookie);
    return groups.includes(category);
  }

  const gpcSignal = gpc ?? (window.navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl;
  if (gpcSignal === true) return false;

  return true;
}
